import React, { memo, useMemo } from 'react';
import { createUseStyles, Loader } from '@Sage/hoi-poi-ui';
import ContentLoader from 'react-content-loader';
import { theme } from '../../lib/styles/theme';

import styles from './styles/loadingOverlay.styles';
const useStyles = createUseStyles(styles, 'LoadingOverlay');

const LoadingOverlay = memo(
    ({
        id,
        useLoaderPlaceholder,
        loaderRowHeight,
        loaderRows = 11,
        loaderColumns,
        useLoaderFullHeight,
        placeholderOptions = { avatars: true },
        gridContext,
    }) => {
        const classes = useStyles();
        const { avatars } = placeholderOptions;

        let baseElement = document;
        if (id) baseElement = document.getElementById(id);

        const baseHeaderHeight = baseElement?.querySelector('.ag-header')?.clientHeight || 0;
        const width = baseElement?.querySelector('.ag-body-viewport')?.clientWidth || 0;
        const height =
            (baseElement?.querySelector('.ag-root')?.clientHeight || 0) + baseHeaderHeight;

        const verticalElements = useMemo(() => {
            const rowHeight = loaderRowHeight || 50;
            const commonElementWidth = 190 + 80;

            let numRows = loaderRows;
            if (useLoaderFullHeight) numRows = Math.ceil(height / rowHeight);

            let numColumns = Math.ceil(width / commonElementWidth);

            if (loaderColumns) numColumns = loaderColumns;

            const circleHeight = 24;
            const firstRectangleWidth = 205;
            const rectangleHeight = 20;
            const rectangleWidth = 240;
            const rectangleRadius = 4;
            const baseMarginLeft = 16;

            const firstCircleY = (rowHeight - circleHeight) / 2 + baseHeaderHeight;
            const firstRectangleY = (rowHeight - rectangleHeight) / 2 + baseHeaderHeight;
            const marginY = rowHeight;

            let elements = [];
            let sumCircleY = firstCircleY;
            let sumRectangleY = firstRectangleY;
            const firstRectangleX = !avatars
                ? baseMarginLeft + baseMarginLeft / 2
                : baseMarginLeft + circleHeight + baseMarginLeft / 2;
            let sumMarginX = firstRectangleX + rectangleWidth;

            for (let i = 0; i < numRows; i++) {
                if (i === 0) {
                    if (avatars)
                        elements.push(
                            <rect
                                key={`fc${i}`}
                                x={baseMarginLeft}
                                y={sumCircleY}
                                rx={circleHeight}
                                ry={circleHeight}
                                width={circleHeight}
                                height={circleHeight}
                            />,
                        );
                    elements.push(
                        <rect
                            key={`fr${i}`}
                            x={firstRectangleX}
                            y={sumRectangleY}
                            rx={rectangleRadius}
                            width={firstRectangleWidth}
                            height={rectangleHeight}
                        />,
                    );
                } else {
                    sumCircleY = sumCircleY + marginY;
                    sumRectangleY = sumRectangleY + marginY;
                    if (avatars)
                        elements.push(
                            <rect
                                key={`c${i}`}
                                x={baseMarginLeft}
                                y={sumCircleY}
                                rx={circleHeight}
                                ry={circleHeight}
                                width={circleHeight}
                                height={circleHeight}
                            />,
                        );
                    elements.push(
                        <rect
                            key={`r${i}`}
                            x={firstRectangleX}
                            y={sumRectangleY}
                            rx={rectangleRadius}
                            width={firstRectangleWidth}
                            height={rectangleHeight}
                        />,
                    );
                }

                for (let a = 0; a < numColumns - 1; a++) {
                    elements.push(
                        <rect
                            key={`r${i}-${a}`}
                            x={sumMarginX}
                            y={sumRectangleY}
                            rx={rectangleRadius}
                            width={rectangleWidth}
                            height={rectangleHeight}
                        />,
                    );
                    sumMarginX = sumMarginX + rectangleWidth + baseMarginLeft * 2;
                    if (a + 1 === numColumns - 1) {
                        sumMarginX = firstRectangleX + rectangleWidth;
                    }
                }
            }
            return elements;
        }, [
            width,
            height,
            loaderRowHeight,
            loaderRows,
            useLoaderFullHeight,
            loaderColumns,
            baseHeaderHeight,
            avatars,
        ]);

        if (useLoaderPlaceholder && !gridContext.current?.startRow) {
            return (
                <div className={classes.overlay}>
                    <div className={classes.fadeMask}>
                        <ContentLoader
                            speed={2}
                            width={width}
                            height={height}
                            viewBox={`0 0 ${width} ${height}`}
                            backgroundColor={theme.colors.actionMinor[50]}
                            foregroundColor={theme.colors.grey[100]}
                        >
                            {verticalElements}
                        </ContentLoader>
                    </div>
                </div>
            );
        }

        return (
            <div className={classes.root}>
                <Loader size="massive" />
            </div>
        );
    },
);

export default LoadingOverlay;
